import gsap from 'gsap';
import 'ol/ol.css'; // Importa los estilos básicos de OpenLayers
import 'ol-ext/dist/ol-ext.css'; // Importa los estilos adicionales de ol-ext
import React, { useState, useEffect, useRef } from 'react'; // Importa React y algunos hooks necesarios
import Map from 'ol/Map'; // Importa la clase Map de OpenLayers
import View from 'ol/View'; // Importa la clase View de OpenLayers
import LayerGroup from 'ol/layer/Group'; // Importa la clase LayerGroup de OpenLayers
import TileLayer from 'ol/layer/Tile'; // Importa la clase TileLayer de OpenLayers
import TileImage from 'ol/source/TileImage'; // Importa la clase TileImage de OpenLayers
import { fromLonLat } from 'ol/proj'; // Importa la función de proyección de coordenadas
import { defaults as defaultControls, ScaleLine, Rotate } from 'ol/control'; // Importa los controles por defecto y la barra de escala
import VectorLayer from 'ol/layer/Vector'; // Importa la clase VectorLayer de OpenLayers
import VectorSource from 'ol/source/Vector'; // Importa la clase VectorSource de OpenLayers
import GeoJSON from 'ol/format/GeoJSON'; // Importa el formato GeoJSON de OpenLayers
import { Style, Fill, Stroke, Circle as CircleStyle, Text } from 'ol/style'; // Importa las clases de estilos de OpenLayers
import LayerSwitcher from 'ol-ext/control/LayerSwitcher'; // Importa el control de capas de ol-ext
import './css/compass.css'; // Importa los estilos de la brújula desde src/css
import 'intro.js/introjs.css';
import introJs from 'intro.js'; // Importa introJs como exportación por defecto

// Función para crear un estilo
const createStyle = (color, width) => new Style({
  stroke: new Stroke({
    color,
    width
  }),
  fill: new Fill({
    color: color
  })
});

// Función para crear capas GeoJSON con estilos personalizados
const createGeoJsonLayer = (url, style, title, visible = true) => {
  const vectorSource = new VectorSource({
    url: url, // URL completa a S3
    format: new GeoJSON()
  });

  return new VectorLayer({
    source: vectorSource,
    style: (feature) => {
      const text = feature.get('Nombre') || feature.get('PopupInfo') || feature.get('Name');
      return new Style({
        stroke: style.getStroke(),
        fill: style.getFill(),
        image: style.getImage(),
        text: new Text({
          font: '16px Calibri,sans-serif',
          fill: new Fill({ color: '#000' }),
          stroke: new Stroke({
            color: '#fff',
            width: 2
          }),
          text: text,
          overflow: true,
          placement: 'line'
        })
      });
    },
    properties: {
      title: title
    },
    visible: visible
  });
};

// Define las capas usando la función createGeoJsonLayer
const conflictosLayer = createGeoJsonLayer('https://geo-griut.s3.us-west-1.amazonaws.com/geojson/Conflictos.geojson', new Style({
  image: new CircleStyle({
    radius: 5,
    fill: new Fill({ color: 'red' }),
    stroke: new Stroke({ color: 'white', width: 1 })
  })
}), 'Conflictos Socio-Ambientales', false);

const puntosHistoricosLayer = createGeoJsonLayer('https://geo-griut.s3.us-west-1.amazonaws.com/geojson/Puntos_Historicos.geojson', new Style({
  image: new CircleStyle({
    radius: 5,
    fill: new Fill({ color: 'yellow' }),
    stroke: new Stroke({ color: 'black', width: 1 })
  })
}), 'Puntos Historicos', false);

const puntosResignificacionLayer = createGeoJsonLayer('https://geo-griut.s3.us-west-1.amazonaws.com/geojson/Puntos_Resignificacion.geojson', new Style({
  image: new CircleStyle({
    radius: 5,
    fill: new Fill({ color: 'purple' }),
    stroke: new Stroke({ color: 'white', width: 1 })
  })
}), 'Puntos de Resignificación', false);

const createWaterLayer = (url, color, title) => {
  return new VectorLayer({
    source: new VectorSource({
      url,
      format: new GeoJSON()
    }),
    style: (feature) => {
      const text = feature.get('Nombre');
      const textStyle = new Text({
        font: 'italic 14px Calibri,sans-serif',
        fill: new Fill({ color: color }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        }),
        text: text,
        overflow: true,
        placement: 'line'
      });

      // Aplicar animación con GSAP al texto
      gsap.fromTo(textStyle, { opacity: 0 }, { opacity: 1, duration: 1 });

      return new Style({
        stroke: new Stroke({
          color: color,
          width: 2
        }),
        text: textStyle
      });
    },
    properties: {
      title: title
    }
  });
};


const esterosLayer = createWaterLayer('https://geo-griut.s3.us-west-1.amazonaws.com/geojson/Esteros.geojson', '#6dceff', 'Esteros');
const riosLayer = createWaterLayer('https://geo-griut.s3.us-west-1.amazonaws.com/geojson/Rios.geojson', '#6dceff', 'Ríos');
const quebradasLayer = createWaterLayer('https://geo-griut.s3.us-west-1.amazonaws.com/geojson/Quebradas.geojson', '#6dceff', 'Quebradas');


const nahuelbutaLayer = new VectorLayer({
  source: new VectorSource({
    url: 'https://geo-griut.s3.us-west-1.amazonaws.com/geojson/Nahuelbuta.geojson', // URL del archivo GeoJSON
    format: new GeoJSON()
  }),
  style: (feature) => {
    const text = feature.get('Nombre') || 'Nawelfuta'; // Obtener el texto de la característica o usar un valor predeterminado
    const textStyle = new Text({
      font: '90px Calibri,sans-serif', // Aumenta el tamaño de la fuente a 60px
      fill: new Fill({ color: 'green' }), // Color de relleno del texto
      stroke: new Stroke({
        color: 'white', // Color del borde del texto
        width: 3 // Grosor del borde del texto
      }),
      text: text, // Texto que se mostrará en el mapa
      overflow: true, // Permitir que el texto se desborde si es necesario
      placement: 'point', // Colocación del texto: en el punto del feature
      textAlign: 'center', // Alineación del texto: centrado
      baseline: 'middle', // Baseline del texto: medio
      rotation: -0.5, // Rotación del texto para crear efecto de arco
      offsetY: 0, // Desplazamiento vertical del texto
      letterSpacing: 100 // Espaciado entre letras
    });

    // Aplicar animación con GSAP al texto
    gsap.fromTo(textStyle, { opacity: 0 }, { opacity: 1, duration: 2 });

    return new Style({
      text: textStyle
    });
  },
  properties: {
    title: 'Nahuelbuta'
  },
  visible: false // Inicialmente no visible
});

const provConceLayer = createGeoJsonLayer('https://geo-griut.s3.us-west-1.amazonaws.com/geojson/Prov_Conce.geojson', new Style({
  stroke: new Stroke({
    color: '#8B4513',
    width: 1,
    lineDash: [5, 5]
  })
}), 'Límite Provincial');

const toponimosLayer = new VectorLayer({
  source: new VectorSource({
    url: 'https://geo-griut.s3.us-west-1.amazonaws.com/geojson/Toponimos.geojson', // URL del archivo GeoJSON
    format: new GeoJSON()
  }),
  style: (feature) => {
    const text = feature.get('Name');
    return new Style({
      text: new Text({
        font: 'bold 16px Calibri,sans-serif', // Fuente en negritas y tamaño de 16px
        fill: new Fill({ color: '#000' }), // Color del texto
        stroke: new Stroke({
          color: '#fff', // Color del borde del texto
          width: 3 // Grosor del borde del texto
        }),
        text: text, // Texto que se mostrará en el mapa
        overflow: true, // Permitir que el texto se desborde si es necesario
        placement: 'point', // Colocación del texto: en el punto del feature
        textAlign: 'center' // Alineación del texto: centrado
      })
    });
  },
  properties: {
    title: 'Toponimia'
  },
  visible: true // Inicialmente visible
});

// Componente del mapa
const MapComponent = () => {
  const mapRef = useRef(null); // Referencia al mapa
  const [layerVisibility, setLayerVisibility] = useState({
    conflictos: false,
    puntosHistoricos: false,
    puntosResignificacion: false
  }); // Estado de visibilidad de las capas
  const initialRotation = useRef(-2); // Rotación inicial del mapa

  // Función para alternar la visibilidad de las capas
  const toggleLayerVisibility = (layerKey) => {
    setLayerVisibility(prevState => ({
      ...prevState,
      [layerKey]: !prevState[layerKey]
    }));
  };

  useEffect(() => {
    if (!mapRef.current) {
      mapRef.current = new Map({
        target: 'map', // ID del elemento DOM donde se renderizará el mapa
        layers: [
          new LayerGroup({
            title: 'Mapa Base',
            layers: [
              new TileLayer({
                source: new TileImage({
                  url: 'https://api.mapbox.com/styles/v1/alejandro00bustos/clv9300mb03go01nugjw2heg1/tiles/256/{z}/{x}/{y}?access_token=pk.eyJ1IjoiYWxlamFuZHJvMDBidXN0b3MiLCJhIjoiY2xmeXE0MW14MGJqNDNmczE2MHhkeGU1cSJ9.atA_HPcGYF4yYvxU-BwVjw',
                  tilePixelRatio: 2
                }),
                title: 'Mapbox'
              })
            ]
          }),
          new LayerGroup({
            title: 'Capas',
            layers: [
              conflictosLayer,
              puntosHistoricosLayer,
              puntosResignificacionLayer,
              esterosLayer,
              nahuelbutaLayer,
              provConceLayer,
              quebradasLayer,
              riosLayer,
              toponimosLayer
            ]
          })
        ],
        view: new View({
          center: fromLonLat([-73.044, -36.826]), // Centro inicial del mapa
          zoom: 10.5, // Nivel de zoom inicial
          rotation: initialRotation.current // Rotación inicial del mapa
        }),
        controls: defaultControls({ zoom: false, rotate: false }).extend([
          new ScaleLine({
            units: 'metric' // Añade una barra de escala métrica
          }),
        ])
      });

      // Añadir el control de capas (LayerSwitcher)
    const layerSwitcher = new LayerSwitcher({
      activationMode: 'click', // Modo de activación por clic
      startActive: false, // No empieza activo
      tipLabel: 'Leyenda', // Etiqueta del botón de la leyenda
      groupSelectStyle: 'none', // Estilo de selección de grupo sin controles de opacidad
      collapseTipLabel: 'Ocultar leyenda' // Etiqueta del botón para ocultar la leyenda
    });
    layerSwitcher.element.id = 'layer-switcher'; // Añade un id al control de capas
    layerSwitcher.element.style.right = '10px'; // Ajusta la posición del control de capas
    layerSwitcher.element.style.top = '50%'; // Ajusta la posición del control de capas
    layerSwitcher.element.style.transform = 'translateY(-50%)'; // Centra verticalmente el control de capas

    mapRef.current.addControl(layerSwitcher); // Añade el control de capas al mapa
    
      
      // Añadir el logo de GRIUT con texto
      const logoContainer = document.createElement('div');
      logoContainer.style.position = 'absolute';
      logoContainer.style.top = '9px'; // Deja margen para los botones de zoom
      logoContainer.style.right = '10px';
      logoContainer.style.zIndex = '1000'; // Asegúrate de que esté por encima del mapa
      logoContainer.style.display = 'flex';
      logoContainer.style.alignItems = 'center';
      logoContainer.style.padding = '5px'; // Añade un poco de padding alrededor del contenido
      logoContainer.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'; // Fondo translúcido
      logoContainer.style.borderRadius = '5px'; // Bordes redondeados

      const griutLogo = document.createElement('img');
      griutLogo.src = 'logo01.png'; // Ruta al logo de GRIUT
      griutLogo.style.width = '50px'; // Tamaño por defecto para dispositivos móviles
      griutLogo.style.height = 'auto'; // Mantén la proporción de la imagen

      const griutText = document.createElement('div');
      griutText.innerHTML = '<b>Grupo de Estudios Interculturales<br>Urbanos y Territoriales</b>'; // Añade el salto de línea usando <br> y pone el texto en negrita usando <b>
      griutText.style.fontSize = '10px'; // Tamaño por defecto para dispositivos móviles
      griutText.style.color = 'black'; // Color del texto
      griutText.style.marginLeft = '3px'; // Añade un margen a la izquierda del texto

      // Ajusta el tamaño para vista de escritorio
      const mediaQuery = window.matchMedia('(min-width: 768px)');
      const handleMediaQueryChange = (e) => {
        if (e.matches) {
          griutLogo.style.width = '90px'; // Tamaño para vista de escritorio
          griutText.style.fontSize = '20px'; // Tamaño del texto para vista de escritorio
        } else {
          griutLogo.style.width = '50px'; // Tamaño para dispositivos móviles
          griutText.style.fontSize = '10px'; // Tamaño del texto para dispositivos móviles
        }
      };
      mediaQuery.addListener(handleMediaQueryChange);
      handleMediaQueryChange(mediaQuery);

      logoContainer.appendChild(griutLogo);
      logoContainer.appendChild(griutText);
      document.getElementById('map').appendChild(logoContainer); // Añade el contenedor del logo al mapa

      // Función para manejar cambios en el nivel de zoom
      const handleZoomChange = () => {
        const zoom = mapRef.current.getView().getZoom();
        const showWaterLayers = zoom >= 11.5; // Muestra las capas de agua solo si el zoom es 14 o mayor
        const showNahuelbutaLayer = zoom >= 11; // Muestra la capa de Nahuelbuta si el zoom es 12 o mayor
        const showToponimosLayer = zoom >= 10.5; // Muestra la capa de Toponimia si el zoom es 10.5 o mayor

        esterosLayer.setVisible(showWaterLayers);
        riosLayer.setVisible(showWaterLayers);
        quebradasLayer.setVisible(showWaterLayers);
        nahuelbutaLayer.setVisible(showNahuelbutaLayer); // Activa la capa de Nahuelbuta a zoom 12
        toponimosLayer.setVisible(showToponimosLayer); // Activa la capa de Toponimia a zoom 10.5
      };

      mapRef.current.getView().on('change:resolution', handleZoomChange); // Añade el listener para cambios de resolución
      handleZoomChange(); // Ajusta la visibilidad inicial basada en el nivel de zoom inicial

      // Actualizar la visibilidad de las capas según el estado
      mapRef.current.getLayers().forEach(group => {
        if (group instanceof LayerGroup) {
          group.getLayers().forEach(layer => {
            const title = layer.get('title');
            if (title) {
              const layerKey = title.toLowerCase();
              if (layerVisibility[layerKey] !== undefined) {
                layer.setVisible(layerVisibility[layerKey]);
              }
            }
          });
        }
      });

             // Añadir la brújula
      const compass = document.getElementById('compass');

      // Función para actualizar la orientación de la brújula
      mapRef.current.getView().on('change:rotation', function () {
        const rotation = mapRef.current.getView().getRotation();
        compass.style.transform = 'rotate(' + (rotation + Math.PI / 2) + 'rad)'; // Ajustar la brújula para que apunte al este
      });

      // Añadir evento click a la brújula para reiniciar la rotación
      compass.addEventListener('click', () => {
        const currentRotation = mapRef.current.getView().getRotation();
        if (currentRotation !== initialRotation.current) {
          mapRef.current.getView().setRotation(initialRotation.current);
        } else {
          mapRef.current.getView().setRotation(0);
        }
      });
    }
        // Configurar intro.js para el tutorial
      const intro = introJs();
      intro.setOptions({
        steps: [
          {
            intro: "Bienvenido a Geo-GRIUT, nuestro visualizador de mapas interactivo. Vamos a guiarte a través de las funcionalidades básicas."
          },
          {
            element: document.querySelector('#map'),
            intro: "Este  mapa  puedes interactuar con diferentes capas y ver información geográfica...",
            position: 'bottom'
          },
          {
            element: document.querySelector('.compass'),
            intro: "Esta es la brújula. Te muestra la orientación actual del mapa. Haz clic en ella para restablecer la orientación.",
            position: 'right'
          },
          {
            element: document.querySelector('#layer-switcher'),
            intro: "Este es el control de capas. Puedes usarlo para alternar la visibilidad de las diferentes capas del mapa.",
            position: 'left'
          },
          {
            intro: "<input type='checkbox' id='dont-show-again'> No volver a mostrar.             Página en Desarrollo 🦖",
            position: 'bottom'
          }
        ],
        showStepNumbers: false,
        showProgress: true,
        scrollToElement: true,
        disableInteraction: true,
        hideNext: false,
        hidePrev: false,
        exitOnOverlayClick: false,
        exitOnEsc: true,
        overlayOpacity: 0.6,
        keyboardNavigation: true,
        doneLabel: 'Listo',
        nextLabel: 'Siguiente',
        prevLabel: 'Anterior',
        skipLabel: 'Saltar',
        scrollTo: 'tooltip'
      });

      // Mostrar el tutorial solo si el usuario no ha marcado "No volver a mostrar"
      if (!localStorage.getItem('introShown')) {
        setTimeout(() => {
          intro.start();
        }, 1000); // Ajusta el tiempo según sea necesario
      }

      // Agregar event listener para el checkbox "No volver a mostrar"
      intro.oncomplete(() => {
        const dontShowAgain = document.getElementById('dont-show-again');
        if (dontShowAgain && dontShowAgain.checked) {
          localStorage.setItem('introShown', 'true');
        }
      });

      intro.onexit(() => {
        const dontShowAgain = document.getElementById('dont-show-again');
        if (dontShowAgain && dontShowAgain.checked) {
          localStorage.setItem('introShown', 'true');
        }
      });

}, [layerVisibility]);

return (
  <div>
    <div id="map" style={{ width: '100%', height: '100vh' }}></div>
    <div id="compass" className="compass"></div>
  </div>
);
};

export default MapComponent;